import { Button, Modal, Typography, Upload, notification } from 'antd';
import axiosClient from 'api/axiosClient';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RcFile } from 'antd/lib/upload/interface';

const { Paragraph, Text } = Typography;

type FileStatus = 'selected' | 'processing' | 'imported' | 'error';

interface BulkCVFile {
  uid: string;
  name: string;
  size: number;
  file: RcFile;
  status: FileStatus;
  error?: string;
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  onImported: () => void;
}

const MAX_FILES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const BulkCVUploadModal = ({ visible, onCancel, onImported }: Props) => {
  const { t } = useTranslation(['recruitment']);
  const [files, setFiles] = useState<BulkCVFile[]>([]);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<{ success: number; failed: number }>();

  useEffect(() => {
    if (!visible) return;
    setFiles([]);
    setSummary(undefined);
  }, [visible]);

  const pendingFiles = useMemo(
    () => files.filter((item) => item.status === 'selected' || item.status === 'error'),
    [files],
  );

  const addFile = (file: RcFile) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      notification.error({ message: t('recruitment:bulk_cv_pdf_only') });
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      notification.error({
        message: t('recruitment:bulk_cv_file_too_large', { name: file.name }),
      });
      return Upload.LIST_IGNORE;
    }

    setFiles((currentFiles) => {
      if (currentFiles.length >= MAX_FILES) {
        notification.warning({ message: t('recruitment:bulk_cv_max_files') });
        return currentFiles;
      }
      const duplicate = currentFiles.some(
        (item) => item.name === file.name && item.size === file.size,
      );
      if (duplicate) return currentFiles;

      return [
        ...currentFiles,
        {
          uid: file.uid,
          name: file.name,
          size: file.size,
          file,
          status: 'selected',
        },
      ];
    });
    setSummary(undefined);
    return Upload.LIST_IGNORE;
  };

  const updateFile = (uid: string, values: Partial<BulkCVFile>) => {
    setFiles((currentFiles) =>
      currentFiles.map((item) => (item.uid === uid ? { ...item, ...values } : item)),
    );
  };

  const removeFile = (uid: string) => {
    setFiles((currentFiles) => currentFiles.filter((item) => item.uid !== uid));
    setSummary(undefined);
  };

  const importOneCV = async (item: BulkCVFile) => {
    const formData = new FormData();
    formData.append('file', item.file);
    const uploadedFile: any = await axiosClient.post(
      '/recruitment/candidate-file/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
    );
    const parsedData = uploadedFile?.data || {};
    const fallbackName = item.name
      .replace(/\.[^/.]+$/, '')
      .replace(/^(cv|resume|profile|ung_tuyen|don_xin_viec)[-_ ]*/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(
        /\b(developer|engineer|tester|fullstack|frontend|backend|qc|qa|intern|fresher|senior|junior|lead)\b/gi,
        '',
      )
      .replace(/\s+/g, ' ')
      .trim();

    const invalidKeywords = [
      /curriculum\s*vitae/i,
      /^resume$/i,
      /^profile$/i,
      /^cv$/i,
      /software\s*(engineer|developer)/i,
      /developer/i,
      /programmer/i,
      /frontend/i,
      /backend/i,
      /fullstack/i,
      /tester/i,
      /quality\s*assurance/i,
      /project\s*manager/i,
      /business\s*analyst/i,
      /ui[/-]?ux\s*designer/i,
      /intern(ship)?/i,
      /fresher/i,
      /junior/i,
      /senior/i,
      /lead/i,
    ];

    const rawName = String(parsedData.candidateName || '').trim();
    const isInvalidName =
      !rawName || rawName.length < 2 || invalidKeywords.some((p) => p.test(rawName));
    const candidateName = (
      isInvalidName && fallbackName ? fallbackName : rawName || fallbackName
    ).trim();
    if (!candidateName) throw new Error(t('recruitment:bulk_cv_missing_name') as string);

    const rawEmail = String(parsedData.email || '').trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    let email = emailRegex.test(rawEmail) ? rawEmail : '';
    if (!email && rawEmail) {
      const match = rawEmail.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
      email = match ? match[0] : '';
    }

    const skills = Array.isArray(parsedData.skills)
      ? parsedData.skills.map((skill: any) => ({
          ...skill,
          rate: typeof skill?.rate === 'number' ? `${skill.rate}/10` : String(skill?.rate || ''),
        }))
      : [];

    await axiosClient.post('/recruitment/candidate', {
      ...parsedData,
      candidateName,
      email: email || parsedData.email || '',
      yearExperience:
        parsedData.yearExperience === undefined || parsedData.yearExperience === null
          ? ''
          : String(parsedData.yearExperience),
      skills,
      jobs: [],
      candidateNotes: [],
      file: uploadedFile,
      source: 'CV upload',
    });
  };

  const handleImport = async () => {
    if (!pendingFiles.length) return;
    setImporting(true);
    setSummary(undefined);
    let success = 0;
    let failed = 0;

    for (const item of pendingFiles) {
      updateFile(item.uid, { status: 'processing', error: undefined });
      try {
        await importOneCV(item);
        success += 1;
        updateFile(item.uid, { status: 'imported', error: undefined });
      } catch (error: any) {
        failed += 1;
        updateFile(item.uid, {
          status: 'error',
          error: error?.message || t('recruitment:bulk_cv_unknown_error'),
        });
      }
    }

    setSummary({ success, failed });
    setImporting(false);
    if (success > 0) onImported();
  };

  const formatSize = (size: number) => `${(size / 1024 / 1024).toFixed(2)} MB`;
  const statusLabel = (status: FileStatus) => {
    const labels: Record<FileStatus, string> = {
      selected: t('recruitment:bulk_cv_status_selected') as string,
      processing: t('recruitment:bulk_cv_status_processing') as string,
      imported: t('recruitment:bulk_cv_status_imported') as string,
      error: t('recruitment:bulk_cv_status_error') as string,
    };
    return labels[status];
  };

  return (
    <Modal
      visible={visible}
      width={860}
      centered
      maskClosable={false}
      title={t('recruitment:bulk_cv_title')}
      footer={null}
      onCancel={importing ? undefined : onCancel}
      destroyOnClose
    >
      <Paragraph type="secondary">{t('recruitment:bulk_cv_description')}</Paragraph>

      <Field>
        <FieldLabel>{t('recruitment:bulk_cv_files')}</FieldLabel>
        <Upload.Dragger
          multiple
          accept=".pdf,application/pdf"
          showUploadList={false}
          beforeUpload={addFile}
          disabled={importing || files.length >= MAX_FILES}
        >
          <UploadTitle>{t('recruitment:bulk_cv_drop_title')}</UploadTitle>
          <Text type="secondary">{t('recruitment:bulk_cv_drop_hint')}</Text>
        </Upload.Dragger>
      </Field>

      {files.length > 0 && (
        <FileSection>
          <FileSectionHeader>
            <Text strong>{t('recruitment:bulk_cv_selected_files')}</Text>
            <Text type="secondary">
              {t('recruitment:bulk_cv_file_count', { count: files.length, max: MAX_FILES })}
            </Text>
          </FileSectionHeader>
          <FileList>
            {files.map((item) => (
              <FileRow key={item.uid}>
                <FileInfo>
                  <Text ellipsis title={item.name}>
                    {item.name}
                  </Text>
                  <Text type="secondary">{formatSize(item.size)}</Text>
                  {item.error && <ErrorText>{item.error}</ErrorText>}
                </FileInfo>
                <Status status={item.status}>{statusLabel(item.status)}</Status>
                {item.status !== 'processing' && item.status !== 'imported' ? (
                  <RemoveButton type="button" onClick={() => removeFile(item.uid)}>
                    {t('recruitment:bulk_cv_remove')}
                  </RemoveButton>
                ) : (
                  <ActionPlaceholder />
                )}
              </FileRow>
            ))}
          </FileList>
        </FileSection>
      )}

      {summary && (
        <ResultSummary hasError={summary.failed > 0}>
          {t('recruitment:bulk_cv_result', {
            success: summary.success,
            failed: summary.failed,
          })}
        </ResultSummary>
      )}

      <Footer>
        <FooterNote>{t('recruitment:bulk_cv_review_note')}</FooterNote>
        <FooterActions>
          <Button disabled={importing} onClick={onCancel}>
            {summary && pendingFiles.length === 0
              ? t('recruitment:bulk_cv_done')
              : t('recruitment:cancel')}
          </Button>
          <PrimaryButton loading={importing} disabled={!pendingFiles.length} onClick={handleImport}>
            {summary && summary.failed > 0
              ? t('recruitment:bulk_cv_retry', { count: pendingFiles.length })
              : t('recruitment:bulk_cv_import', { count: pendingFiles.length })}
          </PrimaryButton>
        </FooterActions>
      </Footer>
    </Modal>
  );
};

export default BulkCVUploadModal;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 18px;

  .ant-select {
    width: 100%;
  }
`;

const FieldLabel = styled.div`
  font-size: var(--txt_font_size_body);
  font-weight: 500;
`;

const UploadTitle = styled.div`
  margin-bottom: 5px;
  font-weight: 500;
`;

const FileSection = styled.div`
  margin-top: 18px;
  border: 1px solid var(--cl_neutral100);
`;

const FileSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--cl_neutral100);
  background: var(--cl_neutral10, #fafafa);
`;

const FileList = styled.div`
  max-height: 260px;
  overflow-y: auto;
`;

const FileRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px 70px;
  gap: 12px;
  align-items: center;
  min-height: 58px;
  padding: 8px 12px;

  & + & {
    border-top: 1px solid var(--cl_neutral100);
  }
`;

const FileInfo = styled.div`
  display: grid;
  min-width: 0;
`;

const ErrorText = styled.span`
  color: #d4380d;
  font-size: 12px;
`;

const Status = styled.span<{ status: FileStatus }>`
  display: inline-block;
  justify-self: start;
  padding: 3px 8px;
  color: ${({ status }) =>
    status === 'imported'
      ? '#237804'
      : status === 'error'
      ? '#a8071a'
      : status === 'processing'
      ? 'var(--cl_primary600)'
      : 'var(--cl_neutral500)'};
  border: 1px solid
    ${({ status }) =>
      status === 'imported'
        ? '#b7eb8f'
        : status === 'error'
        ? '#ffa39e'
        : status === 'processing'
        ? 'var(--cl_primary500)'
        : 'var(--cl_neutral100)'};
  border-radius: var(--bd_radius_default);
  background: ${({ status }) =>
    status === 'imported'
      ? '#f6ffed'
      : status === 'error'
      ? '#fff1f0'
      : status === 'processing'
      ? 'rgba(241, 89, 42, 0.08)'
      : 'var(--cl_white)'};
`;

const RemoveButton = styled.button`
  padding: 0;
  color: var(--cl_primary500);
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const ActionPlaceholder = styled.span``;

const ResultSummary = styled.div<{ hasError: boolean }>`
  padding: 10px 12px;
  margin-top: 16px;
  color: ${({ hasError }) => (hasError ? '#ad4e00' : '#237804')};
  border: 1px solid ${({ hasError }) => (hasError ? '#ffd591' : '#b7eb8f')};
  background: ${({ hasError }) => (hasError ? '#fff7e6' : '#f6ffed')};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 18px;
  margin-top: 18px;
  border-top: 1px solid var(--cl_neutral100);
`;

const FooterNote = styled(Text)`
  max-width: 470px;
  color: var(--cl_neutral500);
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const PrimaryButton = styled(Button)`
  color: var(--cl_white);
  border-color: var(--cl_primary500);
  border-radius: var(--bd_radius_default);
  background-color: var(--cl_primary500);

  &:hover,
  &:focus {
    color: var(--cl_white);
    border-color: var(--cl_primary600);
    background-color: var(--cl_primary600);
  }
`;
